import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { splitPhoneByPrefix, joinPhoneWithPrefix } from '../phone-picker/phone-picker.component';
import { validationMessage } from '../validate-errors/validate-errors.component';
import { ValidateService } from '../../services/validate.service';
import { ToastService } from '../../services/toast.service';
import { CONTACT_ADDRESS_MAX_LENGTH } from '../../validators';

const FIELD_LABELS = {
  Name: 'FORMS_EXTENDED.IMIE_I_NAZWISKO',
  Affinity: 'COMMON.RELATION',
  Address: 'INDIV_CLIENT.ADDRESS_LABEL',
  Others: 'MEDICAL.ADDITIONAL_INFO',
  PhonePrefix: 'USER_DETAILS.PHONE_NUMBER',
  Phone: 'USER_DETAILS.PHONE_NUMBER',
};

@Component({
  selector: 'app-right-contact-panel',
  templateUrl: './right-contact-panel.component.html',
  styleUrls: ['./right-contact-panel.component.scss']
})
export class RightContactPanelComponent implements OnInit {

  @Input() contactList: any = {};
  @Input() currentContactIndex: number = 1;

  @Output() closePanelEvent = new EventEmitter<void>();
  @Output() saveContactEvent = new EventEmitter<{ formData: any, contactIndex: number }>();
  @Output() deleteContactEvent = new EventEmitter<{ contactId: any }>();

  contactForm: FormGroup;

  /** Licznik nieudanych prób zapisu — podbicie odpala potrząśnięcie niepoprawnych pól. */
  contactShakes = 0;

  private prefix: string;

  constructor(
    private fb: FormBuilder,
    private validateService: ValidateService,
    private toast: ToastService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    const prefix = this.prefix = `contact${this.currentContactIndex}`;

    // Numer jest w bazie jednym stringiem — na potrzeby pickera rozbijamy go na
    // kierunkowy i resztę, a przy zapisie sklejamy z powrotem (patrz saveContact()).
    const phone = splitPhoneByPrefix(this.contactList.phone_num);

    this.contactForm = this.fb.group({
      [`${prefix}ID`]: [this.contactList.id || ''],
      [`${prefix}Name`]: [this.contactList.name || '', [Validators.required, Validators.maxLength(32)]],
      // Kierunkowy obowiązkowy: picker sam podstawia domyślny, więc required blokuje
      // tylko sytuację, w której operator wyczyści pole kierunkowego ręcznie.
      [`${prefix}PhonePrefix`]: [phone.prefix, [Validators.required, this.validateService.checkAreaCode]],
      [`${prefix}Phone`]: [phone.number, [
        Validators.required,
        Validators.minLength(4),
        Validators.maxLength(15),
        Validators.pattern('([1-9\\+][0-9]*)$')
      ]],
      [`${prefix}Affinity`]: [this.contactList.family_kind || '', [Validators.required, Validators.maxLength(32)]],
      [`${prefix}Address`]: [this.contactList.address || '', [Validators.maxLength(CONTACT_ADDRESS_MAX_LENGTH)]],
      [`${prefix}Others`]: [this.contactList.others || '', [Validators.maxLength(32)]]
    });

  }

  closePanel() {
    this.closePanelEvent.emit();
  }

  saveContact() {
    if (this.contactForm.valid) {
      const prefix = this.prefix;
      // Na zewnątrz oddajemy dokładnie taki sam kształt jak wcześniej: jeden
      // contactNPhone z pełnym numerem. Kierunkowy jest kontrolką wyłącznie UI,
      // więc nie wypuszczamy go do payloadu (backend go nie zna).
      const formData = { ...this.contactForm.value };
      formData[`${prefix}Phone`] = joinPhoneWithPrefix(
        formData[`${prefix}PhonePrefix`],
        formData[`${prefix}Phone`]
      );
      delete formData[`${prefix}PhonePrefix`];

      this.saveContactEvent.emit({
        formData,
        contactIndex: this.currentContactIndex
      });
    } else {
      this.contactForm.markAllAsTouched();
      this.contactShakes++;
      this.toast.showErrors([this.translate.instant('BACKEND.FIX_FIELDS'), ...this.invalidFieldLines()]);
    }
  }

  private invalidFieldLines(): string[] {
    return Object.keys(FIELD_LABELS)
      .map(field => ({ field, errors: this.contactForm.get(this.prefix + field).errors }))
      .filter(({ errors }) => errors)
      .map(({ field, errors }) => {
        const errorKey = Object.keys(errors)[0];
        const patternReason = field === 'Phone' ? this.translate.instant('FORMS.DIGITS_NO_LEADING_ZERO') : undefined;
        const reason = validationMessage(this.translate, errorKey, errors[errorKey], patternReason)
          || this.translate.instant('FORMS.NOT_VALID_FIELD');
        return `${this.translate.instant(FIELD_LABELS[field])}: ${reason}`;
      })
      .filter((line, index, lines) => lines.indexOf(line) === index);
  }

  deleteContact() {
    const prefix = this.prefix;
    this.deleteContactEvent.emit(
      {
        contactId: this.contactForm.get(`${prefix}ID`).value
      }
    );
  }
}
